/**
 * SEOHead — per-route meta tags, Open Graph, Twitter cards, and JSON-LD.
 *
 * Drop one <SEOHead /> into the top of any public page. Static pages just pass
 * `page`; dynamic pages (event/package detail) pass title/description/image
 * and an optional `jsonLd` schema object built from the fetched data.
 *
 * Note: this is a client-rendered SPA. Google executes JS so these tags are
 * indexed, but social scrapers (WhatsApp/Facebook) only read public/index.html
 * — so deep-link share previews fall back to the static OG tags there.
 */
import { Helmet } from "react-helmet-async";

const SITE = {
  name: "Empire Dance Kenya",
  url: "https://bookings.empiredancekenya.com",
  // TODO: VERIFY — add a real 1200x630 share image at public/branding/og-default.jpg
  ogImage: "https://bookings.empiredancekenya.com/branding/og-default.jpg",
};

// Per-route defaults. Titles/descriptions reflect Empire's real offering:
// dancehall, afrobeat, salsa/kizomba, afro-fusion, kids classes, dance
// fitness, events, packages, studio hire and private sessions.
const PAGE_DEFAULTS = {
  home: {
    path: "/",
    title: "Dance Classes & Wellness in Nairobi | Empire Dance Kenya",
    description:
      "Book dance classes in Nairobi with Empire Dance Kenya — dancehall, afrobeat, salsa, kizomba and afro-fusion for all levels. Adult & kids classes, dance fitness and wellness, private lessons, events, packages and studio hire in Westlands.",
  },
  events: {
    path: "/events",
    title: "Dance Events & Workshops in Nairobi | Empire Dance Kenya",
    description:
      "Upcoming dance events, workshops, showcases and social nights at Empire Dance Kenya in Westlands, Nairobi — afrobeat, dancehall and salsa. Browse what's on and book tickets online.",
  },
  packages: {
    path: "/packages",
    title: "Dance Class Packages & Prices in Nairobi | Empire Dance Kenya",
    description:
      "Dance class packages and passes from Empire Dance Kenya — drop-in, 5-class and 10-class passes. Save on dancehall, afrobeat, salsa and dance fitness classes for adults and kids in Westlands, Nairobi.",
  },
  studio: {
    path: "/studio-booking",
    title: "Dance Studio Hire in Westlands, Nairobi | Empire Dance Kenya",
    description:
      "Hire a dance studio in Westlands, Nairobi by the hour. Empire Dance Kenya's studio suits rehearsals, practice, choreography, auditions, dance fitness and content shoots — book online.",
  },
  private: {
    path: "/private-sessions",
    title: "Private Dance Lessons in Nairobi | Empire Dance Kenya",
    description:
      "Book a private dance lesson in Nairobi with Empire Dance Kenya — one-on-one, couple and small-group sessions for wedding first dances, choreography, dance fitness and beginner coaching across all styles.",
  },
  support: {
    path: "/support",
    title: "Support & Contact | Empire Dance Kenya",
    description:
      "Contact Empire Dance Kenya for help with class bookings, events, packages, studio hire and M-Pesa payments — Westlands, Nairobi.",
  },
};

export default function SEOHead({
  page = "home",
  title,
  description,
  path,
  image,
  noindex = false,
  jsonLd,
}) {
  const d = PAGE_DEFAULTS[page] || PAGE_DEFAULTS.home;
  const finalTitle = title || d.title;
  const finalDescription = description || d.description;
  const canonical = `${SITE.url}${path || d.path || "/"}`;
  const ogImage = image || SITE.ogImage;
  const schemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_KE" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Page-specific JSON-LD */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
